/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package repository

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import models.*
import studio.PaymentFrequency

/**
 * Repository wrapper that synchronizes data between local database and server
 *
 * Strategy:
 * - All writes go to both local database (for offline support) and server (for sync)
 * - Reads prioritize local database for speed, with periodic server syncs
 * - Server is the source of truth for cross-platform consistency
 * - WebSocket connection provides real-time push notifications for cross-platform updates
 */
class SavingsGoalsRepositoryWithSync(
    private val localRepository: SavingsGoalsRepository,
    private val syncService: SavingsGoalsSyncService,
    private val userId: String,
    private val serverUrl: String = "ws://localhost:3000", // Default for development
    // Threaded through to the WS client as `?token=` so the server can
    // verify the JWT subject matches the userId. Null = legacy
    // unauthenticated connect; accepted today, rejected once
    // REQUIRE_WS_AUTH=true is set on the VPS.
    // TODO(ws-auth-phase-2): make this required when REQUIRE_WS_AUTH ships.
    private val getAuthToken: (suspend () -> String?)? = null,
) : SavingsGoalsRepository {

    private var webSocketClient: GoalsSyncWebSocketClient? = null
    private val initScope = CoroutineScope(Dispatchers.Default)

    init {
        // Start WebSocket connection for real-time updates
        // Note: Initial server sync is handled by the wrapper (SavingsGoalsRepositoryFactory)
        // to avoid duplicate calls and potential deadlocks
        initScope.launch {
            try {
                println("🔌 SavingsGoalsRepositoryWithSync: About to start WebSocket sync")
                startWebSocketSync()
                println("✅ SavingsGoalsRepositoryWithSync: WebSocket sync started")
            } catch (e: Exception) {
                println("❌ SavingsGoalsRepositoryWithSync: WebSocket start failed: ${e.message}")
                e.printStackTrace()
            }
        }
    }

    /**
     * Pause background work (WebSocket, coroutines) when app enters background.
     * Call this to reduce memory usage and prevent OS from killing the app.
     */
    fun pause() {
        println("⏸️ SavingsGoalsRepositoryWithSync: Pausing (stopping WebSocket)")
        webSocketClient?.pauseConnection()
    }

    /**
     * Resume background work (reconnect WebSocket) when app enters foreground.
     */
    fun resume() {
        println("▶️ SavingsGoalsRepositoryWithSync: Resuming (reconnecting WebSocket)")
        webSocketClient?.connect()
    }

    /**
     * Start WebSocket connection to receive real-time goal updates from server
     */
    private fun startWebSocketSync() {
        println("🚀 STARTING WebSocket sync initialization...")
        if (webSocketClient != null) {
            println("⚠️ WebSocket already initialized - skipping duplicate initialization")
            return
        }

        try {
            println("🔧 Creating GoalsSyncWebSocketClient for serverUrl=$serverUrl, userId=$userId")
            webSocketClient = GoalsSyncWebSocketClient(
                serverUrl = serverUrl,
                userId = userId,
                getAuthToken = getAuthToken,
                listener =
                    object : GoalSyncListener {
                        override suspend fun onGoalCreated(
                            goalId: String,
                            data: String?,
                        ) {
                            println("🔔 WebSocket: Goal created notification - refreshing from server")
                            // Refresh all goals to get the new one
                            getAllGoals(userId)
                        }

                        override suspend fun onGoalUpdated(
                            goalId: String,
                            data: String?,
                        ) {
                            println("🔔 WebSocket: Goal updated notification - refreshing goal $goalId")
                            // Refresh this specific goal from server
                            refreshGoalFromServer(goalId)
                        }

                        override suspend fun onGoalDeleted(goalId: String) {
                            println("🔔 WebSocket: Goal deleted notification - removing goal $goalId locally")
                            // Remove from local database
                            localRepository.deleteGoal(goalId, userId)
                        }

                        override suspend fun onProgressUpdated(
                            goalId: String,
                            currentAmount: String?,
                        ) {
                            println("🔔 WebSocket: Progress updated notification - refreshing goal $goalId")
                            // Refresh this specific goal to get new progress
                            refreshGoalFromServer(goalId)
                        }

                        override suspend fun onConnected() {
                            println("✅ WebSocket: Connected to server - real-time sync active")
                        }

                        override suspend fun onDisconnected() {
                            println("⚠️ WebSocket: Disconnected from server - will auto-reconnect")
                        }
                    },
            )
            webSocketClient?.connect()
            println("🔌 WebSocket: Connection initiated for userId=$userId")
        } catch (e: Exception) {
            println("❌ WebSocket: Failed to start connection: ${e.message}")
        }
    }

    /**
     * Refresh a specific goal from server and update local database
     * Note: We fetch all goals for simplicity since we don't have a single-goal endpoint
     */
    private suspend fun refreshGoalFromServer(goalId: String) {
        try {
            println("🔄 Refreshing goal $goalId from server")
            getAllGoals(userId)
            println("✅ Refreshed goal $goalId from server")
        } catch (e: Exception) {
            println("⚠️ Failed to refresh goal $goalId from server: ${e.message}")
        }
    }

    /**
     * Get active goals - read from local DB only (U3: local-first architecture)
     * Server sync happens independently via background sync service
     */
    override suspend fun getActiveGoals(userId: String): DatabaseResult<List<SavingsGoal>> {
        // U3: Read from local DB only - server sync handled independently
        return localRepository.getActiveGoals(userId)
    }

    /**
     * Get all goals - fetch from server and update local cache
     */
    override suspend fun getAllGoals(userId: String): DatabaseResult<List<SavingsGoal>> {
        val serverResult = syncService.fetchAllGoalsFromServer(userId)

        return if (serverResult.isSuccess) {
            val serverGoals = serverResult.getOrNull() ?: emptyList()

            // Update local cache with server data
            try {
                serverGoals.forEach { goal ->
                    // Check if goal exists locally
                    val existingResult = localRepository.getGoalById(goal.id)
                    if (existingResult is DatabaseResult.Success && existingResult.data != null) {
                        // Update existing goal
                        localRepository.updateGoal(goal)
                    } else {
                        // Create new goal
                        localRepository.createGoal(goal)
                    }
                }
            } catch (e: Exception) {
                println("⚠️ Failed to update local cache: ${e.message}")
            }

            // Prune stale local goals (goals that exist locally but not on server)
            try {
                val localGoalsResult = localRepository.getAllGoals(userId)
                if (localGoalsResult is DatabaseResult.Success) {
                    val localGoals = localGoalsResult.data
                    val serverGoalIds = serverGoals.map { it.id }.toSet()
                    val staleGoals = localGoals.filter { it.id !in serverGoalIds }

                    if (staleGoals.isNotEmpty()) {
                        println("🧹 Sync: Found ${staleGoals.size} stale local goals to remove")
                        staleGoals.forEach { staleGoal ->
                            println("   🗑️ Removing stale goal: ${staleGoal.name} (${staleGoal.id})")
                            // Delete from local repo only (server already doesn't have them)
                            localRepository.deleteGoal(staleGoal.id, userId)
                        }
                    }
                }
            } catch (e: Exception) {
                println("⚠️ Failed to prune stale goals: ${e.message}")
            }

            DatabaseResult.Success(serverGoals)
        } else {
            // Fallback to local data if server is unavailable
            println("⚠️ Server unavailable, using local data: ${serverResult.exceptionOrNull()?.message}")
            localRepository.getAllGoals(userId)
        }
    }

    /**
     * Get goal by ID - check local first, then server
     */
    override suspend fun getGoalById(goalId: String): DatabaseResult<SavingsGoal?> {
        return localRepository.getGoalById(goalId)
    }

    /**
     * Create goal - save to both local and server
     */
    override suspend fun createGoal(goal: SavingsGoal): DatabaseResult<SavingsGoal> {
        // Save to local database first for immediate feedback
        val localResult = localRepository.createGoal(goal)

        if (localResult is DatabaseResult.Error) {
            return localResult
        }

        // Then sync to server (fire and forget - don't block on server)
        try {
            syncService.syncGoalToServer(goal, userId)
        } catch (e: Exception) {
            // Log but don't fail - local save succeeded
            println("⚠️ Failed to sync goal to server: ${e.message}")
        }

        return localResult
    }

    /**
     * Update goal - update both local and server
     */
    override suspend fun updateGoal(goal: SavingsGoal): DatabaseResult<Unit> {
        val localResult = localRepository.updateGoal(goal)

        if (localResult is DatabaseResult.Error) {
            return localResult
        }

        // Sync to server using dedicated update method
        try {
            syncService.updateGoalOnServer(goal, userId)
        } catch (e: Exception) {
            println("⚠️ Failed to sync goal update to server: ${e.message}")
        }

        return localResult
    }

    /**
     * Update goal progress - update both local and server
     */
    override suspend fun updateGoalProgress(goalId: String, currentAmount: Double): DatabaseResult<Unit> {
        val localResult = localRepository.updateGoalProgress(goalId, currentAmount)

        if (localResult is DatabaseResult.Error) {
            return localResult
        }

        // Sync progress update to server
        try {
            syncService.updateProgressOnServer(goalId, currentAmount)
        } catch (e: Exception) {
            println("⚠️ Failed to sync progress update to server: ${e.message}")
        }

        return localResult
    }

    /**
     * Update Wiz 3R AI fields locally. The server already persists these via
     * the goal-update endpoint (PUT /api/goals/:id) when the form is saved,
     * so no separate server sync call is needed here — this overload is
     * mainly used by future card-level "Set account type" shortcuts that
     * bypass the goal edit form.
     */
    override suspend fun updateGoalAiFields(
        goalId: String,
        userId: String,
        accountType: GoalAccountType?,
        debtInterestRate: Double?,
        debtRemainingBalance: Double?,
        debtRemainingMonths: Int?,
        debtPaymentFrequency: PaymentFrequency?,
        debtMonthlyEquivalentPayment: Double?,
    ): DatabaseResult<Unit> = localRepository.updateGoalAiFields(
        goalId,
        userId,
        accountType,
        debtInterestRate,
        debtRemainingBalance,
        debtRemainingMonths,
        debtPaymentFrequency,
        debtMonthlyEquivalentPayment,
    )

    /**
     * Mark goal inactive - update both local and server
     */
    override suspend fun markGoalInactive(goalId: String): DatabaseResult<Unit> {
        val localResult = localRepository.markGoalInactive(goalId)

        // Server sync would happen on next full sync

        return localResult
    }

    /**
     * Delete goal - delete from both local and server
     */
    override suspend fun deleteGoal(goalId: String, userId: String): DatabaseResult<Unit> {
        val localResult = localRepository.deleteGoal(goalId, userId)

        if (localResult is DatabaseResult.Error) {
            return localResult
        }

        // Delete from server
        try {
            syncService.deleteGoalOnServer(goalId, userId)
        } catch (e: Exception) {
            println("⚠️ Failed to delete goal from server: ${e.message}")
        }

        return localResult
    }

    /**
     * Get goals by category
     */
    override suspend fun getGoalsByCategory(userId: String, category: GoalCategory): DatabaseResult<List<SavingsGoal>> {
        return localRepository.getGoalsByCategory(userId, category)
    }

    /**
     * Get goals nearing deadline
     */
    override suspend fun getGoalsNearingDeadline(userId: String): DatabaseResult<List<SavingsGoal>> {
        return localRepository.getGoalsNearingDeadline(userId)
    }

    /**
     * Observe active goals (Flow-based)
     */
    override fun observeActiveGoals(userId: String): Flow<List<SavingsGoal>> {
        return localRepository.observeActiveGoals(userId)
    }

    // Delegate recommendations methods to local repository
    override suspend fun saveRecommendations(recommendations: List<SavingsRecommendation>, userId: String): DatabaseResult<Unit> {
        return localRepository.saveRecommendations(recommendations, userId)
    }

    override suspend fun getRecommendationsForGoal(goalId: String): DatabaseResult<List<SavingsRecommendation>> {
        return localRepository.getRecommendationsForGoal(goalId)
    }

    override suspend fun getUnappliedRecommendations(goalId: String): DatabaseResult<List<SavingsRecommendation>> {
        return localRepository.getUnappliedRecommendations(goalId)
    }

    override suspend fun markRecommendationApplied(recommendationId: Long): DatabaseResult<Unit> {
        return localRepository.markRecommendationApplied(recommendationId)
    }

    override suspend fun deleteRecommendationsForGoal(goalId: String): DatabaseResult<Unit> {
        return localRepository.deleteRecommendationsForGoal(goalId)
    }

    // Delegate progress history methods to local repository
    override suspend fun recordContribution(goalId: String, userId: String, amount: Double, date: LocalDate, notes: String?): DatabaseResult<Unit> {
        return localRepository.recordContribution(goalId, userId, amount, date, notes)
    }

    override suspend fun getProgressHistory(goalId: String): DatabaseResult<List<GoalContribution>> {
        return localRepository.getProgressHistory(goalId)
    }

    override suspend fun getTotalContributions(goalId: String): DatabaseResult<Double> {
        return localRepository.getTotalContributions(goalId)
    }

    override suspend fun getMonthlyContributions(goalId: String): DatabaseResult<List<MonthlyContribution>> {
        return localRepository.getMonthlyContributions(goalId)
    }

    override suspend fun deleteProgressHistory(goalId: String): DatabaseResult<Unit> {
        return localRepository.deleteProgressHistory(goalId)
    }

    /**
     * Manually trigger sync from server to fetch latest goals from other platforms
     * Call this when user pulls to refresh or when you want to check for updates
     */
    suspend fun refreshFromServer(): DatabaseResult<Unit> {
        return try {
            println("🔄 Manual refresh: Fetching latest goals from server for userId=$userId")
            val result = getAllGoals(userId)
            when (result) {
                is DatabaseResult.Success -> {
                    println("✅ Manual refresh: Successfully synced ${result.data.size} goals")
                    DatabaseResult.Success(Unit)
                }
                is DatabaseResult.Error -> {
                    println("❌ Manual refresh failed: ${result.message}")
                    DatabaseResult.Error(result.exception, result.message)
                }
            }
        } catch (e: Exception) {
            println("❌ Manual refresh exception: ${e.message}")
            DatabaseResult.Error(e, "Failed to refresh from server: ${e.message}")
        }
    }
}
